import { useCallback, useEffect, useRef, useState } from "react";
import { useConversationsApi } from "../api/conversationsApi";

export function useConversations() {
  const api = useConversationsApi();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cursorDateRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const finishedRef = useRef(false);

  const fetchConversations = useCallback(async (date) => {
    try {
      setLoading(true);
      const data = await api.getAll(date);
      setConversations(data);
      finishedRef.current = false;
      cursorDateRef.current = date;
      setError(null);
      return data;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchFilteredConversations = useCallback(async (filters) => {
    try {
      setLoading(true);
      const data = await api.getFilteredConversations(filters);
      setConversations(data);

      cursorDateRef.current = null;
      finishedRef.current = true;
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchMoreConversations = async () => {
    if (!cursorDateRef.current || loadingMoreRef.current || finishedRef.current)
      return;

    try {
      loadingMoreRef.current = true;

      const nextCursor = new Date(cursorDateRef.current);
      nextCursor.setDate(nextCursor.getDate() - 3);

      const conv = await api.getAll(nextCursor);

      if (conv.length === 0) {
        finishedRef.current = true;
        return;
      }

      setConversations((prev) => [...prev, ...conv]);

      cursorDateRef.current = nextCursor;
    } catch (err) {
      setError(err.message);
    } finally {
      loadingMoreRef.current = false;
    }
  };

  const summarizeConversation = async (id) => {
    try {
      const analysis = await api.summarize(id);
      setConversations((prev) => prev.map((conversation) =>
        conversation.id === id
          ? { ...conversation, summary: analysis.summary, analyses: [analysis], analysisPending: false }
          : conversation,
      ));
      return analysis;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const deleteConversation = async (id) => {
    await api.delete(id);
    setConversations((prev) => prev.filter((c) => c.id !== id));
  };

  useEffect(() => {
    const cursorDate = new Date();
    cursorDate.setHours(0, 0, 0, 0);
    cursorDate.setDate(cursorDate.getDate() + 1);

    fetchConversations(cursorDate);
  }, [fetchConversations]);

  return {
    conversations,
    setConversations,
    summarizeConversation,
    deleteConversation,
    loading,
    error,
    fetchConversations,
    fetchFilteredConversations,
    fetchMoreConversations,
  };
}
